/**
 * The MaJourneys AI orb — "Aurora".
 *
 * A navy sphere with a conic sweep of gold and violet turning behind the
 * glass, a fixed specular highlight so it reads as an object rather than a
 * flat disc, and a spark that twinkles. Chosen from /ai-orb-lab.
 *
 * The idle loops are slow on purpose (6-9s). A fast idle animation on a
 * permanently visible control is what makes an interface feel agitated; the
 * speed belongs in `thinking`, where it is telling the guest something.
 *
 * The stylesheet is injected by the component rather than living in
 * tailwind.css so the orb stays self-contained wherever it is dropped.
 */

const ORB_CSS = `
.mj-orb {
  position: relative;
  display: inline-grid;
  place-items: center;
  border-radius: 9999px;
  border: none;
  isolation: isolate;
  overflow: hidden;
  background: radial-gradient(120% 120% at 30% 22%, #2A2378 0%, #171246 46%, #0D0A30 100%);
  /* Lighter (owner, 2026-09-08). At 0.55 the orb dropped a dark pool that
     was heavier than the search bar it sits beside, so the pair read as two
     things at two different heights instead of one row. */
  box-shadow:
    0 6px 16px -8px rgba(17,14,57,0.3),
    inset 0 1px 1px rgba(255,255,255,0.22);
  animation: mjOrbBreathe 6s ease-in-out infinite;
}
@keyframes mjOrbBreathe {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.035); }
}

/* The aurora, blurred and turning behind the glass. */
.mj-orb::before {
  content: "";
  position: absolute;
  inset: -35%;
  z-index: 1;
  background: conic-gradient(from 0deg,
    rgba(229,178,101,0) 0deg,
    rgba(229,178,101,0.55) 60deg,
    rgba(124,92,255,0.45) 150deg,
    rgba(229,178,101,0) 240deg,
    rgba(124,92,255,0.30) 320deg,
    rgba(229,178,101,0) 360deg);
  filter: blur(10px);
  animation: mjOrbSpin 9s linear infinite;
}
@keyframes mjOrbSpin { to { transform: rotate(360deg); } }

/* Fixed light source, so the sphere keeps its form while the aurora moves. */
.mj-orb::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  z-index: 3;
  pointer-events: none;
  background:
    radial-gradient(42% 34% at 32% 22%, rgba(255,255,255,0.40) 0%, rgba(255,255,255,0) 70%),
    radial-gradient(80% 70% at 50% 118%, rgba(229,178,101,0.22) 0%, rgba(229,178,101,0) 70%);
}

.mj-orb-glyph {
  position: relative;
  z-index: 4;
  color: #FFFFFF;
  line-height: 1;
  animation: mjOrbTwinkle 4.5s ease-in-out infinite;
  filter: drop-shadow(0 0 6px rgba(229,178,101,0.55));
}
@keyframes mjOrbTwinkle {
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.94; }
  50%      { transform: scale(1.12) rotate(45deg); opacity: 1; }
}

.mj-orb:hover { animation-duration: 3.2s; }
.mj-orb:active { transform: scale(0.96); }

/* Working: the same orb, hurried, with a halo pulsing out of it. */
.mj-orb-thinking { animation-duration: 2.4s; }
.mj-orb-thinking::before { animation-duration: 2.6s; }
.mj-orb-thinking .mj-orb-glyph { animation-duration: 1.6s; }
.mj-orb-halo {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  pointer-events: none;
  animation: mjOrbHalo 1.8s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}
@keyframes mjOrbHalo {
  0%   { box-shadow: 0 0 0 0 rgba(229,178,101,0.5); }
  70%  { box-shadow: 0 0 0 14px rgba(229,178,101,0); }
  100% { box-shadow: 0 0 0 0 rgba(229,178,101,0); }
}

@media (prefers-reduced-motion: reduce) {
  .mj-orb, .mj-orb::before, .mj-orb-glyph, .mj-orb-halo { animation: none !important; }
}
`;

export interface AiOrbProps {
  /** Rendered size in px. The glyph scales with it. */
  size: number;
  /** Speeds the orb up and adds the halo, for when the concierge is working. */
  thinking?: boolean;
  className?: string;
}

/**
 * Presentational only — it renders a `<span>`, so wrap it in whatever
 * button or link the surface needs and put the label there.
 */
export function AiOrb({ size, thinking = false, className = "" }: AiOrbProps) {
  return (
    <span
      className={`relative inline-grid place-items-center ${className}`}
      style={{ width: size, height: size }}
    >
      {/* href + precedence lets React 19 hoist this to <head> and dedupe it,
          so N orbs on a page still ship one copy of the stylesheet. */}
      <style href="mj-ai-orb" precedence="default">
        {ORB_CSS}
      </style>
      {thinking && <span aria-hidden className="mj-orb-halo" />}
      <span
        aria-hidden
        className={`mj-orb${thinking ? " mj-orb-thinking" : ""}`}
        style={{ width: size, height: size }}
      >
        <span className="mj-orb-glyph" style={{ fontSize: Math.round(size * 0.38) }}>
          ✦
        </span>
      </span>
    </span>
  );
}
